import classNames from 'classnames';
import React from 'react';

import type { AccountBalance, AccountType, Currency, LocalCurrencyBalance } from '../../../generated-types';
import useTranslation, { formatJsx } from '../../../hooks/useTranslation';
import { currencyFormatter } from '../../shared/numberFormat';
import { BsuDetails } from '../AccountViewTypes';
import AccountBalanceGraph from './AccountBalanceGraph';

import './AccountBalanceView.scss';

interface Props {
    balance: AccountBalance | LocalCurrencyBalance;
    currency: Currency;
    accountType: AccountType;
    bsuDetails?: BsuDetails;
    ageOver34?: boolean;
}

const AccountBalanceView: React.FC<Props> = ({ balance, currency, accountType, bsuDetails, ageOver34 = false }) => {
    const translated = useTranslation();

    const isLargeAmount = (amount: number | undefined) => (amount ?? 0) >= 1000000 || (amount ?? 0) <= -100000;

    if ('creditLimit' in balance && balance.creditLimit) {
        if (balance.accountingBalance >= 0) {
            return (
                <>
                    <span
                        data-testid="visible-balance"
                        itemProp="value"
                        {...{ content: String(balance.accountingBalance) }}
                        className={classNames({
                            'large-balance': isLargeAmount(balance.accountingBalance),
                        })}
                    >
                        {currencyFormatter(balance.accountingBalance, currency)}
                    </span>
                    <div className="sgw-account-details__description">
                        {formatJsx(
                            translated.accountView.creditUnused,
                            <strong>{currencyFormatter(balance.creditLimit, currency)}</strong>,
                        )}
                    </div>
                </>
            );
        }
        return (
            <>
                <span
                    data-testid="visible-balance"
                    itemProp="value"
                    {...{ content: String(balance.accountingBalance) }}
                    className={classNames({
                        'large-balance': isLargeAmount(balance.accountingBalance),
                    })}
                >
                    {currencyFormatter(balance.accountingBalance, currency)}
                </span>
                <AccountBalanceGraph used={-balance.accountingBalance} total={balance.creditLimit} />
                <div className="sgw-account-details__description">
                    {formatJsx(
                        translated.accountView.creditUsed,
                        <strong>{currencyFormatter(balance.availableBalance, currency)}</strong>,
                        <strong>{currencyFormatter(balance.creditLimit, currency)}</strong>,
                    )}
                </div>
            </>
        );
    }

    if (accountType === 'BSU') {
        if (ageOver34) {
            return (
                <span
                    itemProp="value"
                    {...{ content: String(bsuDetails?.balance) }}
                    className={classNames({
                        'large-balance': isLargeAmount(balance.accountingBalance),
                    })}
                >
                    {currencyFormatter(balance.accountingBalance ?? 0, currency)}
                </span>
            );
        }
        return (
            <>
                <span
                    itemProp="value"
                    {...{ content: String(bsuDetails?.balance) }}
                    className={classNames({
                        'large-balance': isLargeAmount(bsuDetails?.balance),
                    })}
                >
                    {currencyFormatter(bsuDetails?.balance ?? 0, currency)}
                </span>
                <AccountBalanceGraph used={bsuDetails?.balance ?? 0} total={bsuDetails?.maxAmountToSave ?? 0} />
                <div className="sgw-account-details__description">
                    {bsuDetails?.maxedOutBsu ? (
                        <>
                            {!bsuDetails?.underOneYearBeforeFullBsu
                                ? translated.bsuDetails.maxAmountThisYear
                                : translated.bsuDetails.bsuMaxAmountReached}
                        </>
                    ) : (
                        formatJsx(
                            translated.bsuDetails.amountSaved,
                            <strong>{currencyFormatter(bsuDetails?.amountToSave ?? 0, currency)}</strong>,
                        )
                    )}
                </div>
            </>
        );
    }

    const blockedAmount = 'blockedAmount' in balance ? balance.blockedAmount ?? 0 : 0;
    const calculatedBalance = balance.availableBalance + blockedAmount;
    return (
        <>
            <span className="balance-title">{translated.availableForUse}</span>
            <span
                data-testid="visible-balance"
                itemProp="value"
                {...{ content: String(calculatedBalance) }}
                className={classNames({
                    'large-balance': isLargeAmount(calculatedBalance),
                })}
            >
                {currencyFormatter(calculatedBalance, currency)}
            </span>
        </>
    );
};

export default AccountBalanceView;
